import * as React from 'react';
import { useFormik } from 'formik';
import { css } from '@emotion/core';
import { Str } from '../../localization/translationKeys';
import { FLEX_ROW, FLEX_COLUMN } from '../../layout/flexBox';
import { FORM_COLUMN } from '../../forms/styles/form';
import { btnStyle } from '../../forms/styles/button';
import LocalInput from '../../localization/LocalInput';
import { INPUT } from '../../forms/styles/input';
import LocalBtn from '../../localization/LocalBtn';
import { forgotPasswordSecurityAnswerFormValidation } from '../formValidations';
import { ERROR_MSG } from '../../forms/styles/messages';
import Msg from '../../localization/Msg';
import { FIELDSET_DEFAULT } from '../../layout/fieldSetStyle';
import { COLORS } from '../../globalStyle/colors';
import { useAppState } from '../../core/redux/hooks';


const styles = {
  header:   css`margin-bottom: 16px;`,
  fieldset: FIELDSET_DEFAULT,
  form:     [FLEX_COLUMN, FORM_COLUMN],
  errorMsg: [FLEX_ROW, ERROR_MSG, css`align-items: center;`],
  input:    INPUT(),
  btn:      btnStyle.primary(COLORS.GREEN),
};


interface Props {
  className?: string;
  emailAddress: string;
  question: string;
  answer: string;
  sendPasswordResetEmailRequest: (email: string) => void;
  incorrectSecurityAnswer: boolean;
  isLoading: boolean;
}


/**
 * ForgotPasswordSecurityAnswerForm: a component that provides a form for the user to provide the answer to their
 * security question in support of their identity. If answered correctly, the app will send a request to the API to have
 * a password reset link emailed to the user.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
// TODO: compare hashed form answer rather than plaintext once returning the hashed answer is implemented in API
const ForgotPasswordSecurityAnswerForm: React.FC<Props> = props => {
  const { handleSubmit, handleChange, isValid, values } = useFormik({
    initialValues:    {
      answer: '',
    },
    onSubmit:         () => props.sendPasswordResetEmailRequest(props.emailAddress),
    validationSchema: forgotPasswordSecurityAnswerFormValidation(props.answer),
    validateOnChange: false,
  });

  const dir = useAppState(state => state.locale.textDirection);

  return (
    <div className={props.className}>
      <Msg css={styles.header} str={Str.AnswerSecurityQuestion} />
      <h4 dir={dir}>{props.question}</h4>
      <fieldset disabled={props.isLoading} css={styles.fieldset}>
        <form onSubmit={handleSubmit} css={styles.form}>
          {
            (props.incorrectSecurityAnswer || !isValid) && (
              <div css={styles.errorMsg}>
                <Msg str={Str.IncorrectAnswer} />
              </div>
            )
          }
          <LocalInput
            placeholderTranslation={Str.Answer}
            name='answer'
            type='text'
            value={values.answer}
            css={styles.input}
            onChange={handleChange}
            autoComplete='off'
          />
          <LocalBtn str={Str.Continue} type='submit' css={styles.btn} />
        </form>
      </fieldset>
    </div>
  );
};

export default ForgotPasswordSecurityAnswerForm;
